import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Briefcase, Users, Building, IndianRupee, Loader2, RefreshCw,
  TrendingUp, Target, Award, MapPin, Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPlacementsPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPlacements: 11500,
    avgSalary: 35000,
    placementRate: 85,
    partneredCompanies: 150
  });
  const [recentPlacements, setRecentPlacements] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Mock data - can be connected to real API
      setRecentPlacements([
        { id: 1, student: 'Rahul Kumar', company: 'TCS', position: 'Software Developer', salary: '₹4.5 LPA', date: '2026-04-01' },
        { id: 2, student: 'Priya Sharma', company: 'Infosys', position: 'Data Analyst', salary: '₹3.8 LPA', date: '2026-03-28' },
        { id: 3, student: 'Amit Singh', company: 'Wipro', position: 'System Admin', salary: '₹3.2 LPA', date: '2026-03-25' },
        { id: 4, student: 'Sneha Patel', company: 'HCL', position: 'Web Developer', salary: '₹4.0 LPA', date: '2026-03-22' },
      ]);
    } catch (error) {
      toast.error('Failed to load placements data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-placements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Placements Dashboard</h1>
          <p className="text-slate-500 mt-1">Track student placements and company partnerships</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Total Placements</p>
              <p className="text-3xl font-bold mt-1">{stats.totalPlacements.toLocaleString()}+</p>
            </div>
            <Award className="w-8 h-8 text-emerald-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-100 text-sm">Avg. Salary</p>
              <p className="text-3xl font-bold mt-1">₹{stats.avgSalary.toLocaleString()}</p>
            </div>
            <IndianRupee className="w-8 h-8 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-100 text-sm">Placement Rate</p>
              <p className="text-3xl font-bold mt-1">{stats.placementRate}%</p>
            </div>
            <Target className="w-8 h-8 text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-100 text-sm">Partner Companies</p>
              <p className="text-3xl font-bold mt-1">{stats.partneredCompanies}+</p>
            </div>
            <Building className="w-8 h-8 text-amber-200" />
          </div>
        </div>
      </div>

      {/* Recent Placements */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Recent Placements</h3>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Company</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Position</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Salary</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {recentPlacements.map((placement) => (
              <tr key={placement.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-medium text-sm">{placement.student.charAt(0)}</span>
                    </div>
                    <span className="font-medium text-slate-900">{placement.student}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{placement.company}</td>
                <td className="px-4 py-3 text-slate-600">{placement.position}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm font-medium">
                    {placement.salary}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500 text-sm">{placement.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPlacementsPage;
